import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, Phone, Edit2, RefreshCw, FolderOpen, Plus,
  ExternalLink, Trash2, Search, X, Loader2, Briefcase, Calendar,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_TYPES = {
  individual: 'Individual', business: 'Business', law_firm: 'Law Firm',
  bail_bond: 'Bail Bond Company', corporate: 'Corporate', government: 'Government', other: 'Other',
};

const STATUS_COLOR = {
  NEW: 'bg-sky-500/20 text-sky-300 border-sky-500/40',
  PENDING_REVIEW: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  ASSIGNED: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  ACTIVE: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  EVIDENCE_COLLECTION: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  AWAITING_CLIENT_INFO: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  UNDER_REVIEW: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
  COMPLETED: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  CLOSED: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

/**
 * Iter121D — Client Profile page with the new authoritative
 * `client_case_assignments` relationship.
 *
 * Route: /clients/:clientId/view
 */
const ClientViewPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...authHeaders, 'Content-Type': 'application/json' };

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const [assignedCases, setAssignedCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(true);

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [allCases, setAllCases] = useState([]);
  const [allCasesLoading, setAllCasesLoading] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');
  const [pickedCaseId, setPickedCaseId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState('');

  const loadClient = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}`, {
        headers: authHeaders, credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setClient(await res.json());
    } catch (e) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const loadAssignedCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/cases`, {
        headers: authHeaders, credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAssignedCases(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load assigned cases');
      setAssignedCases([]);
    } finally {
      setCasesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  useEffect(() => { loadClient(); loadAssignedCases(); }, [loadClient, loadAssignedCases]);

  const openAssignDialog = async () => {
    setPickedCaseId('');
    setCaseSearch('');
    setShowAssignDialog(true);
    if (allCases.length === 0) {
      setAllCasesLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/cases`, {
          headers: authHeaders, credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch cases');
        const data = await res.json();
        setAllCases(Array.isArray(data) ? data : (data.cases || []));
      } catch (e) {
        toast.error('Could not load case list');
      } finally {
        setAllCasesLoading(false);
      }
    }
  };

  const handleAssign = async () => {
    if (!pickedCaseId) {
      toast.error('Pick a case to assign');
      return;
    }
    setAssigning(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/cases`, {
        method: 'POST', headers: jsonHeaders, credentials: 'include',
        body: JSON.stringify({ case_id: pickedCaseId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        toast.error(data.detail || 'This case is already assigned to the client');
        return;
      }
      if (!res.ok) {
        toast.error(data.detail || 'Failed to assign case');
        return;
      }
      toast.success('Case assigned to client');
      setShowAssignDialog(false);
      await loadAssignedCases();
    } catch (err) {
      toast.error('Failed to assign case');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (caseRow) => {
    const label = caseRow.case_number || caseRow.id;
    if (!window.confirm(`Remove case "${label}" from this client? The case, its evidence, notes and logs will NOT be deleted — only the client access will be revoked.`)) return;
    setRemovingId(caseRow.id);
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}/cases/${caseRow.id}`, {
        method: 'DELETE', headers: authHeaders, credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to remove');
        return;
      }
      toast.success('Client removed from case');
      await loadAssignedCases();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setRemovingId('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading client…
      </div>
    );
  }
  if (!client) {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <p className="text-slate-300 mb-4">Client not found.</p>
        <Button onClick={() => navigate('/clients')} className="bg-blue-600 hover:bg-blue-700 text-white">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Clients
        </Button>
      </div>
    );
  }

  const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || '—';
  const type = CLIENT_TYPES[client.client_type] || 'Individual';

  const assignedIds = new Set(assignedCases.map((c) => c.id));
  const q = caseSearch.trim().toLowerCase();
  const availableCases = allCases
    .filter((c) => !assignedIds.has(c.id))
    .filter((c) => {
      if (!q) return true;
      const bag = [c.case_number, c.title, c.case_type, c.status, c.client_name].filter(Boolean).join(' ').toLowerCase();
      return bag.includes(q);
    })
    .slice(0, 200);

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="client-view-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-slate-600 !text-slate-200 hover:bg-slate-800" onClick={() => navigate('/clients')} data-testid="client-view-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-white">Client Details</h1>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate(`/clients/${client.id}/edit`)} data-testid="client-view-edit-btn">
          <Edit2 className="w-4 h-4 mr-1" /> Edit Client
        </Button>
      </div>

      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center">
            <User className="w-8 h-8 text-white" />
          </div>
          <div className="min-w-0">
            <h4 className="text-xl font-semibold text-white break-words">{name}</h4>
            {(client.business_name || client.company_name) && (
              <p className="text-slate-400 break-words">{client.business_name || client.company_name}</p>
            )}
            <p className="text-sm text-slate-400">{type}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded">
            <Mail className="w-5 h-5 text-blue-400" />
            <div className="min-w-0">
              <p className="text-xs text-slate-400">Email</p>
              <p className="text-sm text-white truncate">{client.primary_email || client.email || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded">
            <Phone className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-xs text-slate-400">Phone</p>
              <p className="text-sm text-white">{client.primary_phone || client.phone || '—'}</p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-slate-700/30 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-semibold text-slate-300">Portal Access</h5>
            <span className={`px-2 py-1 rounded text-xs font-medium ${client.portal_enabled ? 'bg-green-500/20 text-green-300' : 'bg-slate-600 text-slate-300'}`}>
              {client.portal_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          {client.portal_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-slate-400">Username: </span>
                <span className="text-white break-all">{client.portal_username || client.primary_email || '—'}</span>
              </div>
              <div>
                <span className="text-slate-400">Last Login: </span>
                <span className="text-white">{client.last_login ? new Date(client.last_login).toLocaleString() : 'Never'}</span>
              </div>
            </div>
          )}
        </div>

        {client.notes && (
          <div className="p-4 bg-slate-700/30 rounded-lg">
            <h5 className="text-sm font-semibold text-slate-300 mb-2">Notes</h5>
            <p className="text-sm text-slate-300 whitespace-pre-wrap break-words">{client.notes}</p>
          </div>
        )}
      </div>

      {/* Iter121D — ASSIGNED CASES */}
      <section
        className="bg-slate-800 rounded-lg border border-slate-700 p-4 sm:p-6"
        data-testid="client-assigned-cases-section"
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <FolderOpen className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-white truncate">Assigned Cases</h2>
            <span
              className="ml-1 px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded font-mono"
              data-testid="assigned-cases-count"
            >
              {assignedCases.length}
            </span>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={openAssignDialog}
            data-testid="client-assign-case-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Assign Case
          </Button>
        </div>

        {casesLoading ? (
          <div className="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading cases…
          </div>
        ) : assignedCases.length === 0 ? (
          <div className="p-6 text-center text-slate-400 border border-dashed border-slate-700 rounded-lg" data-testid="client-no-cases">
            <p className="text-sm">No cases have been assigned to this client yet.</p>
            <p className="text-xs mt-1 text-slate-500">Click "Assign Case" to grant the client portal access to one of your cases.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {assignedCases.map((c) => {
              const statusCls = STATUS_COLOR[c.status] || 'bg-slate-600/40 text-slate-300 border-slate-500/40';
              return (
                <div
                  key={c.id}
                  className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 flex flex-col gap-2"
                  data-testid={`assigned-case-card-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-400 font-mono truncate" data-testid={`assigned-case-number-${c.id}`}>
                        {c.case_number || c.id}
                      </p>
                      <p className="text-sm font-semibold text-white break-words leading-snug">
                        {c.title || 'Untitled Case'}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded border ${statusCls} whitespace-nowrap flex-shrink-0`}
                      data-testid={`assigned-case-status-${c.id}`}
                    >
                      {c.status?.replace(/_/g, ' ') || 'STATUS'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                    {c.case_type && (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="w-3 h-3" /> {c.case_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {fmtDate(c.created_at)}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-1 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 !text-slate-200 hover:bg-slate-800 flex-1 sm:flex-none"
                      onClick={() => navigate(`/cases/${c.id}`)}
                      data-testid={`open-assigned-case-${c.id}`}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" /> Open Case
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-700/60 !text-red-300 hover:bg-red-900/30"
                      onClick={() => handleRemove(c)}
                      disabled={removingId === c.id}
                      data-testid={`remove-assigned-case-${c.id}`}
                    >
                      {removingId === c.id
                        ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Removing…</>
                        : <><Trash2 className="w-4 h-4 mr-1" /> Remove</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Assign Case dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3 sm:p-4" data-testid="assign-case-dialog">
          <div className="bg-slate-900 rounded-lg border border-slate-700 w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-700">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2 min-w-0">
                <FolderOpen className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span className="truncate">Assign Case to {name}</span>
              </h3>
              <button
                onClick={() => setShowAssignDialog(false)}
                className="text-slate-400 hover:text-white flex-shrink-0 ml-2"
                data-testid="assign-case-dialog-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={caseSearch}
                  onChange={(e) => setCaseSearch(e.target.value)}
                  placeholder="Search by case number, title, type…"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                  data-testid="assign-case-search-input"
                  autoFocus
                />
              </div>

              {allCasesLoading ? (
                <div className="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading cases…
                </div>
              ) : availableCases.length === 0 ? (
                <div className="p-6 text-center text-slate-400 border border-dashed border-slate-700 rounded">
                  {q
                    ? 'No cases match your search.'
                    : 'No unassigned cases available.'}
                </div>
              ) : (
                <div className="border border-slate-700 rounded max-h-80 overflow-y-auto divide-y divide-slate-800">
                  {availableCases.map((c) => {
                    const selected = pickedCaseId === c.id;
                    const statusCls = STATUS_COLOR[c.status] || 'bg-slate-600/40 text-slate-300 border-slate-500/40';
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setPickedCaseId(c.id)}
                        className={`w-full text-left p-3 transition-colors ${selected ? 'bg-blue-900/40 ring-1 ring-inset ring-blue-500/60' : 'hover:bg-slate-800/60'}`}
                        data-testid={`assign-case-option-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-slate-400 font-mono truncate">{c.case_number || c.id}</p>
                            <p className="text-sm font-semibold text-white truncate">{c.title || 'Untitled Case'}</p>
                            {c.case_type && (
                              <p className="text-xs text-slate-500 truncate">{c.case_type.replace(/_/g, ' ')}</p>
                            )}
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded border ${statusCls} whitespace-nowrap flex-shrink-0`}>
                            {c.status?.replace(/_/g, ' ') || '—'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end px-4 sm:px-6 py-3 border-t border-slate-700">
              <Button
                variant="outline"
                onClick={() => setShowAssignDialog(false)}
                className="border-slate-600 !text-slate-200"
                data-testid="assign-case-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={!pickedCaseId || assigning}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="assign-case-confirm-btn"
              >
                {assigning
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning…</>
                  : <><Plus className="w-4 h-4 mr-2" /> Assign Case</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientViewPage;
